/**
 * <ia-klaviyo> — Klaviyo signup form.
 *
 * Attributes:
 *   data-list-id             Klaviyo list that receives email signups.
 *   data-sms-list-id         Optional. Klaviyo list that receives phone (SMS) signups.
 *                            When set, phone numbers go to this list instead of only
 *                            the email list. When blank, everything goes to data-list-id
 *                            in a single request (original behaviour).
 *   data-phone-country-code  Optional. Prepended to phone numbers entered without a
 *                            leading "+" (e.g. "+1" turns (555) 010-9999 into +15550109999).
 *   data-error-message       Fallback error copy.
 */
const KLAVIYO_SUBSCRIBE_URL = 'https://manage.kmail-lists.com/ajax/subscriptions/subscribe'

class IAKlaviyo extends HTMLElement {
  constructor () {
    super()

    this.form = this.querySelector('form')
    this.submitButton = this.querySelector('button[type="submit"]')
    this.errorElement = this.querySelector('[data-signup-error]')
    this.successElement = this.querySelector('[data-signup-success]')
    this.emailInput = this.querySelector('input[type="email"]')
    this.phoneInput = this.querySelector('input[type="tel"]')
    this.listId = (this.getAttribute('data-list-id') || '').trim()
    this.smsListId = (this.getAttribute('data-sms-list-id') || '').trim()
    this.phoneCountryCode = (this.getAttribute('data-phone-country-code') || '').replace(/\D/g, '')
    this.errorMessageDefault = this.getAttribute('data-error-message') || 'An error occurred. Please try again.'
  }

  connectedCallback () {
    if (!this.form) return
    this.form.addEventListener('submit', this.handleSubmit.bind(this))
  }

  /**
   * Klaviyo only accepts E.164 numbers (+15550109999). Strip spaces, dots,
   * dashes and brackets, turn a "00" international prefix into "+", and if
   * there is still no "+" prepend the configured country code, dropping the
   * national trunk zero (the leading 0 in 07700...). A number that already
   * starts with the country code and is long enough to be international
   * (1 555 010 9999 in the US) is kept as is. With no country code
   * configured the cleaned number is sent as typed and Klaviyo reports it
   * as invalid.
   */
  normalisePhone (raw) {
    if (!raw) return null
    let value = raw.trim().replace(/[\s().-]/g, '')
    if (!value) return null

    if (value.startsWith('00')) value = `+${value.slice(2)}`
    if (value.startsWith('+')) return `+${value.slice(1).replace(/\D/g, '')}`

    const digits = value.replace(/\D/g, '')
    if (!digits) return null
    if (!this.phoneCountryCode) return digits

    const alreadyInternational = digits.startsWith(this.phoneCountryCode) && digits.length >= 11
    if (alreadyInternational) return `+${digits}`

    return `+${this.phoneCountryCode}${digits.replace(/^0+/, '')}`
  }

  /**
   * Build the list of subscribe requests for this submission.
   *
   * Single-list mode (no SMS list configured): one request carrying whatever
   * was entered, exactly as before.
   *
   * Two-list mode: the email list receives a request when an email was
   * entered, the SMS list receives one when a phone was entered. Both
   * identifiers are sent on every request so Klaviyo resolves them to one
   * profile instead of creating an email-only and a phone-only profile.
   */
  buildRequests (email, phone) {
    const identifiers = {}
    if (email) identifiers.email = email
    if (phone) identifiers.phone_number = phone

    if (!this.smsListId) {
      return this.listId ? [{ listId: this.listId, fields: identifiers }] : []
    }

    const requests = []
    if (email && this.listId) {
      requests.push({ listId: this.listId, fields: identifiers })
    }
    if (phone) {
      requests.push({ listId: this.smsListId, fields: { ...identifiers, sms_consent: 'true' } })
    }
    return requests
  }

  async subscribe ({ listId, fields }) {
    const body = new URLSearchParams()
    body.append('g', listId)
    Object.entries(fields).forEach(([key, value]) => body.append(key, value))

    const response = await fetch(KLAVIYO_SUBSCRIBE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Cache-Control': 'no-cache',
      },
      body,
    })

    const data = await response.json()
    if (!data.success) {
      const message = Array.isArray(data.errors) && data.errors.length > 0
        ? data.errors[0]
        : this.errorMessageDefault
      throw new Error(message)
    }
    return data
  }

  showError (message) {
    if (this.successElement) this.successElement.classList.add('ia-hidden')
    if (this.errorElement) {
      this.errorElement.textContent = message
      this.errorElement.classList.remove('ia-hidden')
    }
  }

  showSuccess () {
    if (this.emailInput) this.emailInput.value = ''
    if (this.phoneInput) this.phoneInput.value = ''
    if (this.errorElement) this.errorElement.classList.add('ia-hidden')
    this.form.classList.add('ia-hidden')
    if (this.successElement) this.successElement.classList.remove('ia-hidden')
  }

  setBusy (busy) {
    this.form.setAttribute('aria-busy', busy ? 'true' : 'false')
    if (this.submitButton) this.submitButton.disabled = busy
  }

  async handleSubmit (event) {
    event.preventDefault()
    if (this.form.getAttribute('aria-busy') === 'true') return

    if (this.errorElement) this.errorElement.classList.add('ia-hidden')
    if (this.successElement) this.successElement.classList.add('ia-hidden')
    this.form.classList.remove('ia-hidden')

    const email = this.emailInput?.value.trim() || null
    const phone = this.normalisePhone(this.phoneInput?.value)

    if (!email && !phone) {
      this.showError('Please enter an email or phone number.')
      return
    }

    const requests = this.buildRequests(email, phone)
    if (requests.length === 0) {
      console.error('IAKlaviyo: no Klaviyo list configured for the submitted fields.')
      this.showError(this.errorMessageDefault)
      return
    }

    this.setBusy(true)
    try {
      // Sequential on purpose: the first request creates the profile, the
      // second then matches it by identifier rather than racing to create it.
      for (const request of requests) {
        await this.subscribe(request)
      }
      this.showSuccess()
    } catch (error) {
      console.error('Error submitting Klaviyo form:', error)
      this.showError(error.message || this.errorMessageDefault)
    } finally {
      this.setBusy(false)
    }
  }
}

customElements.define('ia-klaviyo', IAKlaviyo)
